
import Vue, { PropType } from 'vue';
import moment from 'moment-timezone';
import _trim from 'lodash/trim';
import _sortBy from 'lodash/sortBy';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CompanyUserRoleDef, Team, TeamUserRoleDef } from '@run-diffusion/shared';
import { db, functions } from '@/firebase';
import { CompanyTeamsBindingMixin, TeamUserMixin, get$bindFirestoreOptions } from '@/mixins';
import { EMAIL_REGEX, SNACKBAR_STATUS } from '@/constants/constants';
import { titleCase } from '@/filters';
import {
	ASSIGN_FAILURE_MESSAGE,
	CompanyUserRoleOutcome,
	CompanyUserRoleTarget,
	EMPTY_COMPANY_USER_ROLE_TARGET,
	addDialogBody,
	asksForInviteTeam,
	assignNeedsInviteTeam,
	assignNeedsInviteTeamMessage,
	assignOutcome,
	buildAssignPayload,
	isPageLevelAdd,
} from './companyUserRoleDialogState';
import { JUST_ADDED_LIST, noteJustAdded } from '@/utils/justAdded';
import { justAddedDirectoryInviteRow } from '@/views/Team/MembersDirectory/membersDirectoryState';
import ActionsIsland from '@/components/ActionsIsland.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import EnterpriseUpsellWrapper from '@/components/EnterpriseUpsellWrapper/EnterpriseUpsellWrapper.vue';
import RoleAutoAssignExplainer from '@/components/RoleAutoAssignExplainer/RoleAutoAssignExplainer.vue';

/**
 * Assign a company role, owned by TeamPage like every other membership dialog.
 * Opened from a directory row or a member dashboard it is for that one person
 * (the address is fixed and no team is asked for, unless the server answers
 * that the person has neither an account nor a pending invite); opened from
 * the directory's page-level button it takes any address, plus the team to
 * invite it to should it turn out to have no account.
 */
export default Vue.extend({
	name: 'AddCompanyUserRoleDialog',
	mixins: [
		// CompanyTeamsBindingMixin reads `user` and `hasManageTeamsPermission` off TeamUserMixin.
		TeamUserMixin,
		CompanyTeamsBindingMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		target: {
			type: Object as PropType<CompanyUserRoleTarget>,
			default: () => ({ ...EMPTY_COMPANY_USER_ROLE_TARGET }),
		},
	},
	data () {
		return {
			saving: false,
			formValid: true,
			// Set when the server refuses a row-level Add for want of a team to invite to.
			needsInviteTeam: false,

			email: '',
			selectedRoleDefId: null as string | null,
			selectedTeamId: null as string | null,
			selectedTeamRoleDefId: null as string | null, // null = "✨ Automatically assign"

			companyUserRoleDefs: [] as CompanyUserRoleDef[],
			loadingCompanyUserRoleDefs: false,
			// The company's TEAM role defs, for the auto-invite "Assigned Role" picker.
			teamUserRoleDefs: [] as TeamUserRoleDef[],
			loadingTeamUserRoleDefs: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		pageLevel (): boolean {
			return isPageLevelAdd(this.target);
		},
		asksForTeam (): boolean {
			return asksForInviteTeam({ pageLevel: this.pageLevel, needsInviteTeam: this.needsInviteTeam });
		},
		bodyCopy (): string {
			return addDialogBody(this.target);
		},
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		rules () {
			return {
				email: [
					(v: string) => !!_trim(v) || 'Required',
					// Only a typed address is checked: a row's address is already a member's,
					// and one the pattern dislikes must not lock that person out of a role.
					...(this.pageLevel ? [(v: string) => EMAIL_REGEX.test(_trim(v)) || 'Must be a valid email address'] : []),
				],
				team: [
					(v: string) => !!v || 'Required',
				],
			};
		},
		// The store team is offered before (and whether or not) the company's list lands.
		teamPickerItems (): Array<Pick<Team, 'id' | 'name'>> {
			const teams: Team[] = this.allTeams || [];
			const storeTeam: Team | null = this.team || null;
			const hasStoreTeam: boolean = !!storeTeam && teams.some((t: Team) => t.id === storeTeam.id);
			return _sortBy(
				[
					...(storeTeam && !hasStoreTeam ? [storeTeam] : []),
					...teams,
				].map((t: Team) => ({ id: t.id, name: t.name })),
				(t: Pick<Team, 'id' | 'name'>) => (t.name || '').toLowerCase(),
			);
		},
		selectedTeamName (): string {
			const selected = this.teamPickerItems.find((t: Pick<Team, 'id' | 'name'>) => t.id === this.selectedTeamId);
			return selected?.name || `the ${this.displayLabels.team} below`;
		},
		teamPickerLabel (): string {
			return titleCase(this.displayLabels.team);
		},
		teamPickerHint (): string {
			return `This ${this.displayLabels.team} is used only if they are not on any ${this.displayLabels.team} in this company yet.`;
		},
		teamRoleDefItems () {
			return [
				{
					value: null,
					text: '✨ Automatically assign',
				},
				...this.teamUserRoleDefs.map((role: TeamUserRoleDef) => ({
					value: role.id,
					text: role.name,
				})),
			];
		},
		lowestTeamRoleDef (): TeamUserRoleDef | null {
			if (!(this.teamUserRoleDefs || []).length) return null;
			return this.teamUserRoleDefs[this.teamUserRoleDefs.length - 1] as TeamUserRoleDef;
		},
		confirmDisabled (): boolean {
			return !!(
				this.saving ||
				!this.formValid ||
				!_trim(this.email) ||
				!this.selectedRoleDefId ||
				(this.asksForTeam && !this.selectedTeamId)
			);
		},
	},
	watch: {
		value (newVal: boolean) {
			if (newVal) this.init();
			else this.teardown();
		},
	},
	beforeDestroy () {
		this.teardown();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		onTeamRolesUpsell () {
			this.setUpsellDialog({ noTeamRoles: true });
		},
		init () {
			this.saving = false;
			this.needsInviteTeam = false;
			this.email = this.target?.email || '';
			this.selectedRoleDefId = null;
			this.selectedTeamRoleDefId = null;
			this.selectedTeamId = null;
			if (this.$refs.form) this.$refs.form.resetValidation();

			this.bindCompanyUserRoleDefs(this.companyId);
			if (this.pageLevel) this.initTeamPicker();
		},
		// The picker defaults to the selected team, which is what the old page always sent.
		initTeamPicker () {
			this.selectedTeamId = this.team?.id || null;
			this.bindTeamUserRoleDefs(this.companyId);
			this.loadCompanyTeams(this.companyId);
		},
		// Nothing listens while the dialog is closed: it lives as long as TeamPage does.
		teardown () {
			if (this.$firestoreRefs?.companyUserRoleDefs) this.$unbind('companyUserRoleDefs');
			if (this.$firestoreRefs?.teamUserRoleDefs) this.$unbind('teamUserRoleDefs');
			this.companyUserRoleDefs = [];
			this.teamUserRoleDefs = [];
			this.loadingCompanyUserRoleDefs = false;
			this.loadingTeamUserRoleDefs = false;
			this.clearCompanyTeams();
		},
		async bindCompanyUserRoleDefs (companyId: string | null) {
			if (!companyId) return;

			this.loadingCompanyUserRoleDefs = true;
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const roleDefsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind(
					'companyUserRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding company user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load company roles',
					show: true,
				});
			} finally {
				this.loadingCompanyUserRoleDefs = false;
			}
		},
		async bindTeamUserRoleDefs (companyId: string | null) {
			if (!companyId) return;

			this.loadingTeamUserRoleDefs = true;
			try {
				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const roleDefsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');
				await this.$bind(
					'teamUserRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding team user role defs:', error);
			} finally {
				this.loadingTeamUserRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.$emit('input', false);
		},
		onDialogInput (value: boolean) {
			if (!value) this.onResetAndClose();
		},
		async onConfirm () {
			if (this.scimProvisioningLocksMembership) {
				this.onResetAndClose();
				return;
			}
			if (this.saving || !this.companyId || !this.selectedRoleDefId || !_trim(this.email)) return;
			if (!this.$refs.form.validate()) return;

			this.saving = true;
			const sent = { pageLevel: this.pageLevel, needsInviteTeam: this.needsInviteTeam };
			let outcome: CompanyUserRoleOutcome;
			let needsInviteTeam: boolean = false;
			let pendingInvite: boolean = false;
			// As sent: the dialog resets these as it closes
			const email: string = _trim(this.email);
			const inviteTeam: { id: string; name: string } | null = this.selectedTeamId
				? { id: this.selectedTeamId, name: this.selectedTeamName }
				: null;
			try {
				const manageCompanyAdminFunction = functions.httpsCallable('manageCompanyAdmin');
				const result = await manageCompanyAdminFunction(buildAssignPayload({
					email: this.email,
					companyId: this.companyId,
					companyUserRoleDefId: this.selectedRoleDefId,
					...sent,
					inviteTeamId: this.selectedTeamId,
					inviteTeamUserRoleDefId: this.selectedTeamRoleDefId,
					// If company is not enterprise, then don't allow manual team role assignment
					isEnterprise: !!this.companyClubOfferBenefits.isEnterprise,
				}));
				// The onCall wrapper answers with no data at all when the use case throws.
				outcome = assignOutcome(result?.data, this.email);
				needsInviteTeam = assignNeedsInviteTeam(result?.data, sent);
				pendingInvite = !!result?.data?.pendingInvite;
			} catch (error) {
				console.error('Error adding company admin role:', error);
				outcome = { succeeded: false, message: ASSIGN_FAILURE_MESSAGE };
			}
			this.saving = false;

			// Not a dead end: the person is listed but has neither an account nor a
			// pending invite, so ask for the one thing the server is missing.
			if (needsInviteTeam) {
				this.needsInviteTeam = true;
				this.initTeamPicker();
				outcome = { succeeded: false, message: assignNeedsInviteTeamMessage(this.email, this.displayLabels.team) };
			}

			this.updateSnackbar({
				status: outcome.succeeded ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
				message: outcome.message,
				show: true,
			});
			// A refusal (wrong address, a role they already hold) is something to
			// correct, so the dialog stays open with what was typed.
			if (!outcome.succeeded) return;
			// An address typed in with no account became an invitation, a new row the
			// Directory has only once the mirror does: pinned on top until then
			if (sent.pageLevel && pendingInvite && this.companyId) {
				noteJustAdded(JUST_ADDED_LIST.companyMembers(this.companyId), [{
					id: email.toLowerCase(),
					row: justAddedDirectoryInviteRow({ email, invitedIso: moment().toISOString(), team: inviteTeam }),
				}]);
			}
			this.$emit('success');
			this.onResetAndClose();
		},
	},
	components: {
		ActionsIsland,
		BaseButton,
		DialogContent,
		DialogOrBottomSheet,
		EnterpriseUpsellWrapper,
		RoleAutoAssignExplainer,
	},
});
